import '@/styles/globals.css';
import '@/styles/multiRangeSlider.css';

import { useDispatch, useSelector } from 'react-redux';
import { createWrapper } from 'next-redux-wrapper';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { Navbar, Footer, GoogleAnalytics } from '@unianlx-support/ua-ui-lib';

import { store } from '@/store';
import { isLoggedIn, logout, loggedUserDetails } from '@/services/auth';
import { setUser } from '@/store/slices/userSlice';
import { CallAPI } from '@/services/utils';
import { markEmbeddedApp, pathLooksEmbedded, persistEmbedMode } from '@/utils/embedMode';
import { APP_NAME, ANALYTICS_APP_NAME } from '@/constants/app';
import ChatOverlayWrapper from '@/components/chatOverlayWrapper';
import { startAskBernoulliBridge } from '@/utils/askBernoulliBridge';

const wrapper = createWrapper(() => store);

function AppAnalytics() {
  const details = loggedUserDetails();
  return (
    <GoogleAnalytics
      userId={details?.id}
      userName={details?.username}
      appName={ANALYTICS_APP_NAME}
    />
  );
}

function AppContent({ Component, pageProps }) {
  const router = useRouter();
  const [isEmbed, setIsEmbed] = useState(() => pathLooksEmbedded(router.asPath || ''));
  const userDetails = loggedUserDetails();

  useEffect(() => {
    const embedded = persistEmbedMode();
    if (embedded) {
      markEmbeddedApp();
      setIsEmbed(true);
    }
  }, []);

  useEffect(() => {
    const onMessage = (event) => {
      if (!event || !event.data || event.data.type !== 'ASK_BERNOULLI_EMBED') return;
      markEmbeddedApp();
      setIsEmbed(true);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Ask Bernoulli (askbernoulli.ai) reads this screen and plays answers on it only when the app is embedded.
  useEffect(() => {
    if (!isEmbed) return undefined;
    return startAskBernoulliBridge();
  }, [isEmbed]);

  return (
    <>
      <AppAnalytics />
      {isEmbed ? null : (
        <div data-scorecard-chrome>
          <Navbar
            isLoggedIn={isLoggedIn}
            logout={logout}
            loggedUserDetails={loggedUserDetails}
            setUser={setUser}
            CallAPI={CallAPI}
            useDispatch={useDispatch}
            useSelector={useSelector}
            appName={APP_NAME}
            enableAnalytics={false}
            showHowItWorks={true}
            howItWorksData={[
              {
                id: 'Single asset',
                name: 'Single asset',
                'video-url':
                  'https://player.vimeo.com/video/1154220759?badge=0&autopause=0&player_id=0&app_id=58479',
                multiple: false,
                pdf_name: ['View PDF for Single asset'],
                pdf_link: [
                  'https://universaanalyticsindiapvtlt-my.sharepoint.com/:b:/g/personal/corp_universaanalytics_com/IQClr99NB5ogSZo7QzoS3J3uAVRO7qtnz4XvOtGfNPry5n4?e=Y6zzIp',
                ],
                pdf_enable: true,
                show_other_info: false,
              },
              {
                id: 'Different Asset',
                name: 'Different Asset',
                'video-url':
                  'https://player.vimeo.com/video/1154225496?badge=0&autopause=0&player_id=0&app_id=58479',
                multiple: false,
                pdf_name: ['View PDF for Different Asset'],
                pdf_link: [
                  'https://universaanalyticsindiapvtlt-my.sharepoint.com/:b:/g/personal/corp_universaanalytics_com/IQD1MdozwyWoTq38VqB3Z1kAATME-1jWE0iK4dTNnVrqcSg?e=neop32',
                ],
                pdf_enable: true,
                show_other_info: false,
              },
            ]}
          />
        </div>
      )}
      <Component {...pageProps} />
      {isEmbed ? null : (
        <div data-scorecard-chrome>
          <Footer appName={APP_NAME} />
          {/* <ChatOverlayWrapper user={userDetails} /> */}
        </div>
      )}
    </>
  );
}

function App({ Component, pageProps }) {
  return (
    <>
      <Head suppressHydrationWarning>
        <title>Cost Effective Risk Mitigation</title>
      </Head>
      <AppContent Component={Component} pageProps={pageProps} />
    </>
  );
}

export default wrapper.withRedux(App);
